/**
 * The UTC time of the audio a camera plays now, which paces its data channel video (see
 * FrameScheduler). The receiver names the RTP timestamp of the last audio frame out of its jitter
 * buffer and when it came out; the server's sync point maps that timestamp to UTC.
 */

import { SECOND_US } from '@/lib/timeUnits';
import type { EpochAudioClock, SyncPoint } from '@/types/player';

/**
 * How long an audio frame takes from the track to the speaker: the audio output device and the
 * system mixer. It varies by device; this is a typical value.
 */
const AUDIO_OUTPUT_LATENCY_MS = 20;

/** The newest audio mapping of a camera's sync points; undefined before the first. */
export function findAudioSyncPoint(syncMap: readonly SyncPoint[]): SyncPoint['audio'] {
    for (let i = syncMap.length - 1; i >= 0; i -= 1) {
        const audio = syncMap[i]?.audio;
        if (audio !== undefined) { return audio; }
    }
    return undefined;
}

/** What the receiver plays now, on the UTC timeline; null when that cannot be told. */
export function readAudioClock(
    receiver: RTCRtpReceiver, syncPoint?: SyncPoint['audio'],
): EpochAudioClock | null {
    const [source] = receiver.getSynchronizationSources();
    const clockRate = receiver.getParameters().codecs[0]?.clockRate;
    if (source === undefined || syncPoint === undefined || !clockRate) { return null; }
    // JS bitwise coerces to signed 32-bit: the wrap-aware distance in RTP ticks.
    const ticks = (source.rtpTimestamp - syncPoint.rtpTs) | 0;
    return {
        utcUs: syncPoint.utcUs + ticks * SECOND_US / clockRate,
        atEpochMs: source.timestamp + AUDIO_OUTPUT_LATENCY_MS,
    };
}
